import { useEffect, useState } from 'react';
import {
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import PhoneAndroidRoundedIcon from '@mui/icons-material/PhoneAndroidRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { authApi } from '../../../services/api/authApi';
import { getStoredReferralCode, clearStoredReferralCode } from '../../../lib/referral';
import {
  AuthDivider,
  authHintSx,
  authInputSx,
  authLabelSx,
  authIconSx,
  authOutlineBtnSx,
  authPrimaryBtnSx,
  authSubtleTextSx,
} from '../authFormTheme';

function RegisterScreen({ onGoLogin, onRegisterSuccess }) {
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralCode] = useState(() => getStoredReferralCode());
  const [referralAdminName, setReferralAdminName] = useState('');

  useEffect(() => {
    if (!referralCode) return;
    authApi
      .validateReferral(referralCode)
      .then((data) => setReferralAdminName(data.adminName))
      .catch(() => setReferralAdminName(''));
  }, [referralCode]);

  const handleRegister = async () => {
    setError('');
    setSuccess('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const fullPhone = phone.startsWith('+') ? phone : `+91${phone.replace(/\D/g, '')}`;
      const payload = {
        phone: fullPhone,
        name: name || `Player ${phone.slice(-4)}`,
        password,
      };
      if (referralCode) payload.referralCode = referralCode;
      const result = await authApi.register(payload);
      clearStoredReferralCode();
      authApi.saveSession(result);
      setSuccess('Account created! Redirecting…');
      onRegisterSuccess?.(result.user);
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={1.6}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {success ? <Alert severity="success">{success}</Alert> : null}
      {referralCode && referralAdminName ? (
        <Alert severity="info">Registering under {referralAdminName}&apos;s area</Alert>
      ) : null}
      <Typography sx={authHintSx}>
        Join Eleven Games and start playing instantly
      </Typography>

      <Typography sx={authLabelSx}>
        <BadgeRoundedIcon sx={authIconSx} fontSize="small" />
        Full name
      </Typography>
      <TextField
        size="small"
        fullWidth
        placeholder="Your name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        sx={authInputSx}
      />

      <Typography sx={authLabelSx}>
        <PhoneAndroidRoundedIcon sx={authIconSx} fontSize="small" />
        Phone number
      </Typography>
      <TextField
        size="small"
        fullWidth
        placeholder="10-digit mobile number"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        inputProps={{ inputMode: 'numeric' }}
        sx={authInputSx}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ color: 'rgba(17,24,39,0.7)', fontWeight: 800 }}>
              +91
            </InputAdornment>
          ),
        }}
      />

      <Typography sx={{ ...authLabelSx, mt: 1 }}>
        <LockRoundedIcon sx={authIconSx} fontSize="small" />
        Set password
      </Typography>
      <TextField
        size="small"
        type={showPassword ? 'text' : 'password'}
        placeholder="Set password"
        fullWidth
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        sx={authInputSx}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => setShowPassword((v) => !v)}
                sx={{ color: 'rgba(17,24,39,0.7)' }}
              >
                {showPassword ? (
                  <VisibilityRoundedIcon fontSize="small" />
                ) : (
                  <VisibilityOffRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Typography sx={authLabelSx}>
        <LockRoundedIcon sx={authIconSx} fontSize="small" />
        Confirm password
      </Typography>
      <TextField
        size="small"
        type={showConfirm ? 'text' : 'password'}
        placeholder="Confirm password"
        fullWidth
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        sx={authInputSx}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => setShowConfirm((v) => !v)}
                sx={{ color: 'rgba(17,24,39,0.7)' }}
              >
                {showConfirm ? (
                  <VisibilityRoundedIcon fontSize="small" />
                ) : (
                  <VisibilityOffRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Typography sx={authSubtleTextSx}>
        By creating an account, you agree to Eleven Games terms and privacy policy.
      </Typography>

      <Button
        variant="contained"
        size="large"
        disabled={loading}
        onClick={handleRegister}
        sx={authPrimaryBtnSx}
      >
        {loading ? 'Creating account…' : 'Register'}
      </Button>

      <AuthDivider>Already registered?</AuthDivider>

      <Button variant="outlined" size="large" onClick={onGoLogin} sx={authOutlineBtnSx}>
        Go to login
      </Button>
    </Stack>
  );
}

export default RegisterScreen;
